/**
 * Model 2.1 Scoring System
 * Uses M2 words with intensity-based bidirectional scoring:
 * - For D, C, T, O: Intensity 4-5 = above 50%, Intensity 1-2-3 = below 50%
 * - For S (Style): Uses M2 additive scoring from 0% baseline
 */

import M2_ASSESSMENT_WORDS from '@/data/m2-assessmentWords';

export interface M21CategoryScores {
  dominance: number;
  extraversion: number;
  patience: number;
  formality: number;
  objectivity: number;
}

interface CategoryAccumulator {
  positiveIntensity: number;  // intensity 4-5 selected
  negativeIntensity: number;  // intensity 1-2-3 selected
  maxPositive: number;        // max possible positive (all 4-5 intensity words)
  maxNegative: number;        // max possible negative (all 1-2-3 intensity words)
}

const CATEGORY_MAP: Record<string, keyof M21CategoryScores> = {
  'dominance': 'dominance',
  'extraversion': 'extraversion',
  'patience': 'patience',
  'formality': 'formality',
  'objectivity': 'objectivity'
};

/**
 * Calculate M2.1 scores using intensity-based bidirectional scoring
 * For D, C, T, O: Intensity 4-5 = above 50%, Intensity 1-2-3 = below 50%
 * For S (Style): Uses M2 additive scoring from 0% baseline
 */
export function calculateM21Scores(selectedWordIds: Set<number>): M21CategoryScores {
  // Initialize accumulators for each category
  const accumulators: Record<string, CategoryAccumulator> = {};
  
  for (const category of Object.keys(CATEGORY_MAP)) {
    accumulators[category] = {
      positiveIntensity: 0,
      negativeIntensity: 0,
      maxPositive: 0,
      maxNegative: 0
    };
  }

  // Track objectivity separately for M2 additive scoring
  let objectivitySelectedSum = 0;
  let objectivityMaxSum = 0;

  // Process all words to calculate max possible and selected intensities
  for (const word of M2_ASSESSMENT_WORDS) {
    const acc = accumulators[word.category];
    if (!acc) continue;

    const intensity = word.intensity;
    const isSelected = selectedWordIds.has(word.id);

    // Style uses M2 additive scoring
    if (word.category === 'objectivity') {
      objectivityMaxSum += intensity;
      if (isSelected) {
        objectivitySelectedSum += intensity;
      }
      continue;
    }

    // D, C, T, O use bidirectional scoring
    if (intensity >= 4) {
      // High intensity words (4-5) push above 50%
      acc.maxPositive += intensity - 3; // 4→1, 5→2
      if (isSelected) {
        acc.positiveIntensity += intensity - 3;
      }
    } else {
      // Low/mid intensity words (1-2-3) push below 50%
      acc.maxNegative += 4 - intensity; // 3→1, 2→2, 1→3
      if (isSelected) {
        acc.negativeIntensity += 4 - intensity;
      }
    }
  }

  // Calculate final scores for each category
  const scores: M21CategoryScores = {
    dominance: 50,
    extraversion: 50,
    patience: 50,
    formality: 50,
    objectivity: 50
  };

  // Calculate bidirectional scores for D, C, T, O
  for (const [category, key] of Object.entries(CATEGORY_MAP)) {
    if (category === 'objectivity') continue; // Handle separately
    
    const acc = accumulators[category];
    
    // Calculate positive shift (0-48 range mapped to words selected)
    let positiveShift = 0;
    if (acc.maxPositive > 0) {
      positiveShift = (acc.positiveIntensity / acc.maxPositive) * 48;
    }

    // Calculate negative shift (0-48 range mapped to words selected)
    let negativeShift = 0;
    if (acc.maxNegative > 0) {
      negativeShift = (acc.negativeIntensity / acc.maxNegative) * 48;
    }

    // Final score: 50 + positive shift - negative shift
    let finalScore = 50 + positiveShift - negativeShift;

    // Clamp to valid range
    finalScore = Math.max(2, Math.min(98, finalScore));
    
    scores[key] = Math.round(finalScore);
  }

  // Calculate Style using M2 additive scoring (0% baseline)
  if (objectivityMaxSum > 0) {
    const objectivityScore = (objectivitySelectedSum / objectivityMaxSum) * 96 + 2;
    scores.objectivity = Math.round(Math.max(2, Math.min(98, objectivityScore)));
  } else {
    scores.objectivity = 2;
  }

  return scores;
}

/**
 * Get word statistics for M2.1 model
 * For D, C, T, O: high = 4-5, low = 1-2-3
 * For S (Style): Uses M2 classification
 */
export function getM21WordStats() {
  const stats: Record<string, { high: number; neutral: number; low: number; total: number }> = {};
  
  for (const category of Object.keys(CATEGORY_MAP)) {
    stats[category] = { high: 0, neutral: 0, low: 0, total: 0 };
  }

  for (const word of M2_ASSESSMENT_WORDS) {
    const stat = stats[word.category];
    if (!stat) continue;
    
    stat.total++;
    
    // Style uses original M2 classification
    if (word.category === 'objectivity') {
      if (word.intensity >= 4) stat.high++;
      else if (word.intensity <= 2) stat.low++;
      else stat.neutral++;
    } else {
      // D, C, T, O: 4-5 = high (above 50%), 1-2-3 = low (below 50%)
      if (word.intensity >= 4) stat.high++;
      else stat.low++;
    }
  }

  return stats;
}

/**
 * Analyze intensity distribution for debugging
 */
export function analyzeM21IntensityDistribution() {
  const distribution: Record<string, Record<number, number>> = {};
  
  for (const category of Object.keys(CATEGORY_MAP)) {
    distribution[category] = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  }

  for (const word of M2_ASSESSMENT_WORDS) {
    if (distribution[word.category]) {
      distribution[word.category][word.intensity]++;
    }
  }

  return distribution;
}
